import { localized } from "@blocks/utils";
import {
	Button,
	Flex,
	Heading,
	Stack,
	Tab,
	TabList,
	TabPanel,
	TabPanels,
	Tabs,
	Text,
} from "@chakra-ui/react";
import { __ } from "@wordpress/i18n";
import React from "react";
import AdvancedControls from "../../../components/common/AdvancedControls";
import InspectorTabs, {
	AdvancedInspectorControls,
	GeneralInspectorControls,
	StyleInspectorControls,
} from "../../../components/common/InspectorTabs";
import Panel from "../../../components/common/Panel";
import ResponsiveControls from "../../../components/common/ResponsiveControls";
import ZStack from "../../../components/common/ZStack";
import {
	AlignCenter,
	AlignLeft,
	AlignRight,
} from "../../../components/icons/Icons";
import {
	ColorPicker,
	IconPicker,
	PopoverDrawer,
	Slider,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from "../../../controls";
import Reset from "../../../controls/common/Reset";

type Props = {
	attributes: any;
	setAttributes: (attributes: any) => void;
	categories?: any[];
	tags?: any[];
	authorOptions?: any[];
};

const InspectorControls: React.ComponentType<Props> = (props) => {
	const {
		attributes: {
			icon,
			iconGap,
			iconSize,
			iconColor,
			dateTypography,
			alignment,
			color,
			blockPadding,
			blockMargin,
			hideOnDesktop,
			hideOnTablet,
			hideOnMobile,
		},
		setAttributes,
	} = props;

	return (
		<>
			<InspectorTabs />
			<GeneralInspectorControls>
				<Panel title={__("Layout", "magazine-blocks")} initialOpen>
					<Stack direction="row" gap={2} alignItems="center">
						{localized.apiStatus === "1" ? (
							<>
								<div>
									<Flex gap="2" align="center">
										<span
											style={{
												display: "inline-block",
												width: 10,
												height: 10,
												borderRadius: "50%",
												backgroundColor:
													localized.apiStatus === "1"
														? "#38A169"
														: "#E53E3E",
												verticalAlign: "middle",
											}}
										/>
										<Heading
											m="0"
											as="h6"
											size="sm"
											fontWeight="medium"
											fontSize="12px"
											color={
												localized.apiStatus === "1"
													? "#38A169"
													: "#E53E3E"
											}
										>
											{__("Connected", "magazine-blocks")}
										</Heading>
									</Flex>
									<Text
										mt="12px !important"
										mb="0"
										fontWeight="normal"
										fontStyle="italic"
										fontSize="12px"
									>
										{__(
											`Current Temp & Location: ${
												localized.temperature ?? ""
											}
											${localized.unit === "metric" ? "°C" : "°F"} at
											${localized.location ?? ""}`,
											"magazine-blocks"
										)}
									</Text>
									<Button
										mt="12px !important"
										fontSize="sm"
										color="white"
										bgColor="7e36f4"
										_hover={{
											color: "white",
											bgColor: "#7e36f4",
										}}
										display="flex"
										alignItems="center"
										onClick={() => {
											window.open(
												"admin.php?page=magazine-blocks#/settings?tab=integrations",
												"_blank"
											);
										}}
									>
										{__("Change", "magazine-blocks")}
									</Button>
								</div>
							</>
						) : (
							<>
								<div>
									<Flex gap="2" align="center">
										<span
											style={{
												display: "inline-block",
												width: 10,
												height: 10,
												borderRadius: "50%",
												backgroundColor:
													localized.apiStatus === "1"
														? "#38A169"
														: "#E53E3E",
												verticalAlign: "middle",
											}}
										/>
										<Heading
											m="0"
											as="h6"
											size="sm"
											fontWeight="medium"
											fontSize="12px"
											color={
												localized.apiStatus === "1"
													? "#38A169"
													: "#E53E3E"
											}
										>
											{__(
												"Disconnected",
												"magazine-blocks"
											)}
										</Heading>
									</Flex>
									<Text
										mt="12px !important"
										mb="0"
										fontWeight="normal"
										fontStyle="italic"
										fontSize="12px"
									>
										{__(
											"Please setup data and weather details below:",
											"magazine-blocks"
										)}
									</Text>
									<Button
										mt="12px !important"
										fontSize="sm"
										color="white"
										bgColor="7e36f4"
										_hover={{
											color: "white",
											bgColor: "#7e36f4",
										}}
										display="flex"
										alignItems="center"
										onClick={() => {
											window.open(
												"admin.php?page=magazine-blocks#/settings?tab=integrations",
												"_blank"
											);
										}}
									>
										{__("Setup", "magazine-blocks")}
									</Button>
								</div>
							</>
						)}
					</Stack>

					<ToggleButtonGroup
						value={alignment}
						onChange={(value) => {
							setAttributes({ alignment: value });
						}}
						label={__("Alignment", "magazine-blocks")}
						responsive
						groupProps={{
							sx: {
								".chakra-button": {
									minH: "32px",
									svg: {
										w: 6,
										h: 6,
									},
								},
							},
						}}
					>
						<ToggleButton value="left">
							<AlignLeft />
						</ToggleButton>
						<ToggleButton value="center">
							<AlignCenter />
						</ToggleButton>
						<ToggleButton value="right">
							<AlignRight />
						</ToggleButton>
					</ToggleButtonGroup>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<>
						<IconPicker
							value={icon}
							onChange={(val) => setAttributes({ icon: val })}
						/>
						<Slider
							label={__("Size", "magazine-blocks")}
							min={0}
							max={50}
							value={iconSize}
							onChange={(val: any) =>
								setAttributes({ iconSize: val })
							}
							responsive
							units={["px", "em", "%"]}
							resetAttributeKey="iconSize"
						/>
						<Slider
							label={__("Gap", "magazine-blocks")}
							min={0}
							max={60}
							value={iconGap}
							onChange={(val: any) =>
								setAttributes({ iconGap: val })
							}
							responsive
							units={["px", "em", "%"]}
							resetAttributeKey="iconGap"
						/>
					</>
				</Panel>
			</GeneralInspectorControls>
			<StyleInspectorControls>
				<Panel
					title={__("Text and Date", "magazine-blocks")}
					initialOpen
				>
					<PopoverDrawer
						label={__("Text Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: color,
											id: "color",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={color}
										resetKey="color"
										onReset={(v) =>
											setAttributes({ color: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={color}
										onChange={(v) =>
											setAttributes({ color: v })
										}
									></ColorPicker>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
					<Typography
						value={dateTypography}
						onChange={(val) =>
							setAttributes({
								dateTypography: val,
							})
						}
						resetAttributeKey="dateTypography"
					/>
				</Panel>
				<Panel title={__("Icon", "magazine-blocks")}>
					<PopoverDrawer
						label={__("Icon Color", "magazine-blocks")}
						trigger={(props) => (
							<PopoverDrawer.Trigger {...props}>
								<ZStack
									items={[
										{
											color: iconColor,
											id: "iconColor",
										},
									]}
								/>
							</PopoverDrawer.Trigger>
						)}
						closeOnFocusOutside
					>
						<Tabs position="relative">
							<TabList>
								<Tab>{__("Normal", "magazine-blocks")}</Tab>
							</TabList>

							<TabPanels>
								<TabPanel>
									<Reset
										saved={iconColor}
										resetKey="iconColor"
										onReset={(v) =>
											setAttributes({ iconColor: v })
										}
										buttonProps={{
											position: "absolute",
											top: "12px",
											right: "10px",
										}}
									></Reset>
									<ColorPicker
										value={iconColor}
										onChange={(v) =>
											setAttributes({ iconColor: v })
										}
									></ColorPicker>
								</TabPanel>
							</TabPanels>
						</Tabs>
					</PopoverDrawer>
				</Panel>
			</StyleInspectorControls>
			<AdvancedInspectorControls>
				<AdvancedControls
					blockMargin={blockMargin}
					setAttributes={setAttributes}
					blockPadding={blockPadding}
				/>
				<ResponsiveControls
					{...{
						hideOnDesktop,
						hideOnTablet,
						hideOnMobile,
						setAttributes,
					}}
				/>
			</AdvancedInspectorControls>
		</>
	);
};

export default InspectorControls;
